import { useKeycloak } from '@react-keycloak/web';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { AuthContext } from '../Contexts/AuthContext';
import CONFIG from '../Services/CONFIG';

const Http = {
  get: async (address, headers) => {
    const res = await fetch(address, { headers });
    if (!res.ok) throw new Error(res.status);
    return res.json();
  },
};

export const KeycloakAuthProvider = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const isAuthenticated = !!keycloak?.authenticated;
  const user = keycloak?.tokenParsed || null;
  const permissionsKey = user?.permissions?.join?.(',');

  console.log('keycloak - initialized:', initialized);
  console.log('keycloak - authenticated:', isAuthenticated);

  const getAccessTokenSilently = useCallback(async () => {
    if (!keycloak) return null;

    try {
      await keycloak.updateToken(30);
    } catch (e) {}

    return keycloak.token || null;
  }, [keycloak]);

  const logout = useCallback(
    async (opts = {}) => {
      if (!keycloak) return;

      const returnTo = opts.returnTo || window.location.origin;
      await keycloak.logout({ redirectUri: returnTo });
    },
    [keycloak],
  );

  const [status, setStatus] = useState({ status: 'connecting' });

  const fetchStatus = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await Http.get(`${CONFIG.SERVER_ORGANIZATION}/health/connect`, headers);

    setStatus({ ...response, address: CONFIG.SERVER_ORGANIZATION });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    async function checkApiStatus() {
      if (!initialized) return;
      if (!isAuthenticated) return;

      try {
        await fetchStatus();
      } catch (error) {
        console.error('API health check error:', error);
      }
    }

    checkApiStatus();
  }, [initialized, isAuthenticated, permissionsKey, fetchStatus]);

  const refreshStatus = useCallback(async () => {
    try {
      await fetchStatus();
    } catch (error) {
      console.error('API status refresh error:', error);
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (!initialized) return;

    const reqInterceptor = axios.interceptors.request.use(
      async (config) => {
        if (!keycloak?.authenticated) return config;

        try {
          await keycloak.updateToken(30);
        } catch (e) {}

        const token = keycloak.token;
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    return () => axios.interceptors.request.eject(reqInterceptor);
  }, [initialized, keycloak]);

  useEffect(() => {
    const resInterceptor = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 401) {
          logout({ returnTo: window.location.origin });
        }
        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(resInterceptor);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        provider: 'keycloak',
        initialized,
        isLoading: !initialized,
        user,
        isAuthenticated,
        getAccessTokenSilently,
        logout,
        login: keycloak.login,
        status,
        refreshStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
